import React, { useEffect, useRef, useState } from "react";
import styles from "./CarouselBookSettings.module.css";

import { useAuth } from "../../Context/AuthContext";
import {
  fetchCarouselBooks,
  createCarouselBook,
  updateCarouselBook,
  deleteCarouselBook,
  proxiedImageUrl,
} from "../../Api/Backend/CarouselBook";
import {
  CAROUSEL_CATEGORIES,
  DEFAULT_CAROUSEL_CATEGORY,
  isValidCarouselCategory,
} from "../../data/carouselBookCategories";

const ROW_OPTIONS = [
  { value: 1, label: "Row 1" },
  { value: 2, label: "Row 2" },
];

const byDisplayOrder = (a, b) => {
  const av = a.displayOrder === "" || a.displayOrder == null ? Number.MAX_SAFE_INTEGER : a.displayOrder;
  const bv = b.displayOrder === "" || b.displayOrder == null ? Number.MAX_SAFE_INTEGER : b.displayOrder;
  return av - bv;
};

export const toCarouselBookRow = (dto) => ({
  carouselBookId: dto.carouselBookId ?? null,
  title: dto.title ?? "",
  author: dto.author ?? "",
  imageUrl: dto.imageUrl ?? "",
  link: dto.link ?? "",
  category: isValidCarouselCategory(dto.category)
    ? dto.category
    : DEFAULT_CAROUSEL_CATEGORY,
  carouselRow: dto.carouselRow ?? 1,
  displayOrder: dto.displayOrder ?? "",
  dirty: false, // unsaved-changes flag (local UI only)
});

export const buildCarouselBookPayload = (item) => ({
  title: item.title,
  author: item.author,
  imageUrl: item.imageUrl,
  link: item.link,
  category: item.category,
  carouselRow:
    item.carouselRow === "" || item.carouselRow == null ? null : Number(item.carouselRow),
  displayOrder:
    item.displayOrder === "" || item.displayOrder == null ? null : Number(item.displayOrder),
});

export const isValidCarouselBook = (item) =>
  !!item.title?.trim() &&
  isValidCarouselCategory(item.category);

const rowKey = (s) => (s.carouselBookId != null ? s.carouselBookId : s._tempId);

export const reorderCarouselBooks = (arr, from, to) => {
  const copy = arr.slice();
  const [item] = copy.splice(from, 1);
  copy.splice(to, 0, item);
  return copy.map((book, index) => ({
    ...book,
    displayOrder: (index + 1) * 10,
  }));
};

const rowOf = (book) => (book.carouselRow === 2 ? 2 : 1);

let tempIdSeq = -1; // negative ids for unsaved rows (never sent to backend)

export default function CarouselBookSettings() {
  const auth = useAuth();

  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [savingAll, setSavingAll] = useState(false);
  const [reordering, setReordering] = useState(false);
  const [dragOverKey, setDragOverKey] = useState(null);
  const [activeCategory, setActiveCategory] = useState(
    DEFAULT_CAROUSEL_CATEGORY
  );

  const dragIndexRef = useRef(null);
  const dragGroupRef = useRef(null);

  useEffect(() => {
    fetchCarouselBooks({
      onSuccess: (data) => {
        const rows = (Array.isArray(data) ? data : [])
          .map(toCarouselBookRow)
          .sort(byDisplayOrder);
        setBooks(rows);
        setLoading(false);
      },
      onError: (error) => {
        console.error("Error fetching carousel books:", error);
        alert("캐러셀 도서를 불러올 수 없습니다.");
        setLoading(false);
      },
    });
  }, []);

  const handleSave = async (item) => {
    if (!isValidCarouselBook(item)) {
      alert("제목과 도서 분류는 모두 필수입니다.");
      return;
    }

    setSavingId(rowKey(item));
    try {
      if (item.carouselBookId != null) {
        const updated = await updateCarouselBook({
          auth,
          carouselBookId: item.carouselBookId,
          payload: buildCarouselBookPayload(item),
        });
        applySaved(item, updated);
      } else {
        const created = await createCarouselBook({
          auth,
          payload: buildCarouselBookPayload(item),
        });
        applySaved(item, created);
      }
      alert("저장되었습니다.");
    } catch (error) {
      console.error("Error saving carousel book:", error);
      alert(error?.message || "저장 중 오류가 발생했습니다.");
    } finally {
      setSavingId(null);
    }
  };

  const applySaved = (item, savedDto) => {
    if (!savedDto) return;
    const saved = toCarouselBookRow(savedDto);
    setBooks((prev) =>
      prev
        .map((s) => (rowKey(s) === rowKey(item) ? { ...saved, _tempId: undefined } : s))
        .sort(byDisplayOrder)
    );
  };

  const handleDelete = async (item) => {
    if (!window.confirm("이 도서를 삭제하시겠습니까?")) return;

    if (item.carouselBookId == null) {
      setBooks((prev) => prev.filter((s) => rowKey(s) !== rowKey(item)));
      return;
    }

    setSavingId(rowKey(item));
    try {
      await deleteCarouselBook({ auth, carouselBookId: item.carouselBookId });
      setBooks((prev) => prev.filter((s) => rowKey(s) !== rowKey(item)));
    } catch (error) {
      console.error("Error deleting carousel book:", error);
      alert(error?.message || "삭제 중 오류가 발생했습니다.");
    } finally {
      setSavingId(null);
    }
  };

  const handleInputChange = (key, field, value) => {
    setBooks((prev) =>
      prev.map((s) => (rowKey(s) === key ? { ...s, [field]: value, dirty: true } : s))
    );
  };

  const handleAddItem = (category, row) => {
    setBooks((prev) => [
      ...prev,
      {
        carouselBookId: null,
        _tempId: tempIdSeq--,
        title: "",
        author: "",
        imageUrl: "",
        link: "",
        category,
        carouselRow: row,
        displayOrder: "",
        dirty: true,
      },
    ]);
  };

  // Persist existing rows whose displayOrder changed after a reorder.
  const persistOrderChanges = async (oldList, newList) => {
    const oldByKey = new Map(oldList.map((s) => [rowKey(s), s]));
    const changed = newList.filter(
      (s) =>
        s.carouselBookId != null &&
        oldByKey.get(rowKey(s))?.displayOrder !== s.displayOrder
    );
    if (changed.length === 0) return;

    setReordering(true);
    try {
      await Promise.all(
        changed.map((s) =>
          updateCarouselBook({
            auth,
            carouselBookId: s.carouselBookId,
            payload: buildCarouselBookPayload(s),
          })
        )
      );
      const changedKeys = new Set(changed.map((s) => rowKey(s)));
      setBooks((prev) =>
        prev.map((s) => (changedKeys.has(rowKey(s)) ? { ...s, dirty: false } : s))
      );
    } catch (error) {
      console.error("Error saving new order:", error);
      alert(error?.message || "순서 저장 중 오류가 발생했습니다.");
    } finally {
      setReordering(false);
    }
  };

  const handleSaveAll = async () => {
    const dirtyRows = books.filter((s) => s.dirty);
    if (dirtyRows.length === 0) return;

    const invalid = dirtyRows.filter((r) => !isValidCarouselBook(r));
    if (invalid.length > 0) {
      alert("제목 또는 도서 분류가 비어있는 행이 있습니다. 먼저 채워주세요.");
      return;
    }

    setSavingAll(true);
    try {
      const results = await Promise.all(
        dirtyRows.map(async (r) => {
          const saved =
            r.carouselBookId != null
              ? await updateCarouselBook({
                  auth,
                  carouselBookId: r.carouselBookId,
                  payload: buildCarouselBookPayload(r),
                })
              : await createCarouselBook({
                  auth,
                  payload: buildCarouselBookPayload(r),
                });
          return { key: rowKey(r), saved };
        })
      );

      setBooks((prev) =>
        prev
          .map((s) => {
            const res = results.find((r) => r.key === rowKey(s));
            return res?.saved
              ? { ...toCarouselBookRow(res.saved), _tempId: undefined }
              : s;
          })
          .sort(byDisplayOrder)
      );
      alert("모든 변경사항이 저장되었습니다.");
    } catch (error) {
      console.error("Error saving all:", error);
      alert(error?.message || "일괄 저장 중 오류가 발생했습니다.");
    } finally {
      setSavingAll(false);
    }
  };

  // === Drag reorder, scoped within a single (category, row) block ===
  const handleDragStart = (groupId, index) => (e) => {
    dragIndexRef.current = index;
    dragGroupRef.current = groupId;
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", String(index));
  };

  const handleDragOver = (key) => (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (dragOverKey !== key) setDragOverKey(key);
  };

  const handleDrop = (groupId, groupItems) => (index) => (e) => {
    e.preventDefault();
    const from = dragIndexRef.current;
    const fromGroup = dragGroupRef.current;
    dragIndexRef.current = null;
    dragGroupRef.current = null;
    setDragOverKey(null);
    // Only reorder within the same row block; cross-row moves use the Row dropdown.
    if (from == null || fromGroup !== groupId || from === index) return;
    if (groupItems.some((item) => !isValidCarouselBook(item))) {
      alert("순서를 변경하기 전에 제목과 도서 분류를 모두 입력해 주세요.");
      return;
    }

    const reordered = reorderCarouselBooks(groupItems, from, index);
    const byKey = new Map(reordered.map((s) => [rowKey(s), s]));
    setBooks((prev) => prev.map((s) => byKey.get(rowKey(s)) ?? s));
    persistOrderChanges(groupItems, reordered);
  };

  const handleDragEnd = () => {
    dragIndexRef.current = null;
    dragGroupRef.current = null;
    setDragOverKey(null);
  };

  const dirtyCount = books.filter((s) => s.dirty).length;

  const renderBookRow = (book, index, groupId, groupItems) => {
    const key = rowKey(book);
    return (
      <tr
        key={key}
        onDragOver={handleDragOver(key)}
        onDrop={handleDrop(groupId, groupItems)(index)}
        className={dragOverKey === key ? styles.dragOverRow : undefined}
      >
        <td>
          <span
            className={styles.dragHandle}
            draggable
            onDragStart={handleDragStart(groupId, index)}
            onDragEnd={handleDragEnd}
            title="드래그하여 순서 변경"
          >
            ⠿
          </span>
        </td>
        <td>
          <textarea
            value={book.title}
            onChange={(e) => handleInputChange(key, "title", e.target.value)}
            className={styles.titleArea}
            rows={2}
          />
        </td>
        <td>
          <input
            type="text"
            value={book.author}
            onChange={(e) => handleInputChange(key, "author", e.target.value)}
            className={styles.input}
          />
        </td>
        <td>
          <input
            type="text"
            value={book.imageUrl}
            onChange={(e) => handleInputChange(key, "imageUrl", e.target.value)}
            className={styles.input}
            placeholder="https://… 또는 images/…"
          />
        </td>
        <td className={styles.previewCell}>
          {book.imageUrl ? (
            <img
              src={proxiedImageUrl(book.imageUrl)}
              alt=""
              className={styles.previewImg}
              onError={(e) => {
                e.currentTarget.style.visibility = "hidden";
              }}
              onLoad={(e) => {
                e.currentTarget.style.visibility = "visible";
              }}
            />
          ) : (
            <span className={styles.previewEmpty}>—</span>
          )}
        </td>
        <td>
          <input
            type="text"
            value={book.link}
            onChange={(e) => handleInputChange(key, "link", e.target.value)}
            className={styles.input}
          />
        </td>
        <td>
          <select
            value={book.category}
            onChange={(e) => handleInputChange(key, "category", e.target.value)}
            className={styles.input}
            aria-label="도서 분류"
          >
            {CAROUSEL_CATEGORIES.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </td>
        <td>
          <select
            value={rowOf(book)}
            onChange={(e) => handleInputChange(key, "carouselRow", Number(e.target.value))}
            className={styles.input}
          >
            {ROW_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </td>
        <td>
          <input
            type="number"
            value={book.displayOrder}
            onChange={(e) => handleInputChange(key, "displayOrder", e.target.value)}
            className={styles.input}
          />
        </td>
        <td>
          <button
            onClick={() => handleSave(book)}
            className={styles.saveButton}
            disabled={savingId === key}
          >
            {savingId === key ? "저장 중..." : "Save"}
          </button>
          <button onClick={() => handleDelete(book)} className={styles.deleteButton}>
            Delete
          </button>
        </td>
      </tr>
    );
  };

  const renderRowBlock = (rowNum) => {
    const groupId = `${activeCategory}|${rowNum}`;
    const items = books
      .filter((b) => b.category === activeCategory && rowOf(b) === rowNum)
      .sort(byDisplayOrder);

    return (
      <div className={styles.rowBlock} key={rowNum}>
        <h2 className={styles.rowBlockTitle}>Row {rowNum}</h2>
        <div className={styles.tableWrapper}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th aria-label="순서 변경" />
                <th>Title</th>
                <th>Author</th>
                <th>Image URL</th>
                <th>Preview</th>
                <th>Link</th>
                <th>도서 분류</th>
                <th>Row</th>
                <th>Order</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.length > 0 ? (
                items.map((book, index) => renderBookRow(book, index, groupId, items))
              ) : (
                <tr>
                  <td colSpan={10} className={styles.emptyRow}>
                    이 행에 등록된 도서가 없습니다.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <button
          onClick={() => handleAddItem(activeCategory, rowNum)}
          className={styles.addButton}
        >
          + Add Book to Row {rowNum}
        </button>
      </div>
    );
  };

  return (
    <div className={styles.container}>
      <h1 className={styles.header}>메인페이지 도서 등록</h1>
      <p className={styles.description}>
        공개 도서 분류별로 도서를 관리합니다. 좌측 핸들을 드래그하여 순서를 조정하세요.
        {reordering && " (순서 저장 중...)"}
      </p>

      <div className={styles.toolbar}>
        <div className={styles.categoryTabs}>
          {CAROUSEL_CATEGORIES.map((tab) => (
            <button
              key={tab.value}
              type="button"
              className={`${styles.categoryTab}${activeCategory === tab.value ? " " + styles.active : ""}`}
              onClick={() => setActiveCategory(tab.value)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {dirtyCount >= 2 && (
          <div className={styles.saveAllBar}>
            <span className={styles.saveAllCount}>저장되지 않은 변경 {dirtyCount}건</span>
            <button
              className={styles.saveAllButton}
              onClick={handleSaveAll}
              disabled={savingAll}
            >
              {savingAll ? "저장 중..." : "Save All"}
            </button>
          </div>
        )}
      </div>

      {loading ? (
        <p className={styles.description}>불러오는 중...</p>
      ) : (
        <>
          {renderRowBlock(1)}
          {renderRowBlock(2)}
        </>
      )}
    </div>
  );
}
